import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { clearLocalLeads } from "../lib/leadCache";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("ls_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [booting, setBooting] = useState(!!localStorage.getItem("ls_token"));

  useEffect(() => {
    const token = localStorage.getItem("ls_token");
    if (!token) {
      setBooting(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("ls_user", JSON.stringify(r.data));
      })
      .catch(async (err) => {
        // Only clear credentials and cache if the server explicitly rejects the session (401)
        // On network drop, timeout, or server restart, keep the local session intact
        if (err?.response?.status === 401) {
          try {
            await clearLocalLeads();
          } catch (cacheErr) {
            console.warn("Error clearing cache on 401:", cacheErr);
          }
          localStorage.removeItem("ls_token");
          localStorage.removeItem("ls_user");
          setUser(null);
        }
      })
      .finally(() => setBooting(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const payload = {
      email: typeof email === "string" ? email.toLowerCase().trim() : email,
      password: password
    };

    const { data } = await api.post("/auth/login", payload);
    
    // Clear any residual leads left from another user on the same device
    await clearLocalLeads().catch(() => {});

    localStorage.setItem("ls_token", data.access_token);
    localStorage.setItem("ls_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { 
      name: name.trim(), 
      email: email.toLowerCase().trim(), 
      password 
    });
    return data;
  }, []);

  const sendLoginOtp = useCallback(async (email) => {
    const { data } = await api.post("/auth/send-login-otp", { 
      email: email.toLowerCase().trim() 
    });
    return data;
  }, []);

  const verifyLoginOtp = useCallback(async (email, otp) => {
    const { data } = await api.post("/auth/verify-login-otp", {
      email: email.toLowerCase().trim(),
      otp: otp.trim()
    });

    if (data.access_token) {
      await clearLocalLeads().catch(() => {});
      localStorage.setItem("ls_token", data.access_token);
      localStorage.setItem("ls_user", JSON.stringify(data.user));
      setUser(data.user);
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await clearLocalLeads();
    } catch (err) {
      console.warn("Failed to clear local cache during logout:", err);
    }
    localStorage.removeItem("ls_token");
    localStorage.removeItem("ls_user");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      booting, 
      login, 
      register, 
      sendLoginOtp, 
      verifyLoginOtp, 
      logout, 
      setUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}